"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';
import { Spinner } from './spinner.mjs';

const buttonVariants = tv({
  base: [
    "relative inline-flex items-center justify-center isolate align-middle appearance-none",
    "border-0 bg-transparent",
    "min-w-0 rounded-none font-semibold whitespace-nowrap select-none",
    "cursor-pointer outline-none",
    "transition-[background-color,border-color,color,box-shadow] duration-200",
    "disabled:cursor-not-allowed",
    "[&_svg]:text-[1.1em] [&_svg]:w-[1.1em] [&_svg]:h-[1.1em]"
  ],
  variants: {
    variant: {
      solid: [
        "focus-visible:outline-offset-2",
        "disabled:text-fg-disabled disabled:bg-bg-disabled",
        "disabled:hover:text-fg-disabled disabled:hover:bg-bg-disabled"
      ],
      outline: [
        "border border-gray-a7 text-gray-text",
        "hover:bg-gray-a2",
        "focus-visible:outline-gray-default focus-visible:outline-offset-2",
        "data-[selected=true]:bg-gray-default data-[selected=true]:border-gray-default data-[selected=true]:text-gray-fg",
        "data-[selected=true]:hover:bg-gray-emphasized data-[selected=true]:hover:border-gray-emphasized",
        "disabled:border-border-disabled disabled:text-fg-disabled",
        "disabled:hover:bg-transparent disabled:hover:border-border-disabled disabled:hover:text-fg-disabled"
      ],
      ghost: [
        "text-gray-text",
        "hover:bg-gray-a3",
        "data-[selected=true]:bg-gray-a3",
        "focus-visible:outline-gray-default focus-visible:outline-offset-2",
        "disabled:text-fg-disabled",
        "disabled:hover:bg-transparent disabled:hover:text-fg-disabled"
      ],
      subtle: [
        "bg-gray-a3 text-gray-text",
        "hover:bg-gray-a4",
        "focus-visible:outline-gray-default focus-visible:outline-offset-2",
        "disabled:bg-bg-disabled disabled:text-fg-disabled",
        "disabled:hover:bg-bg-disabled disabled:hover:text-fg-disabled"
      ],
      link: [
        "h-auto px-0 min-w-0 align-baseline",
        "disabled:text-border-disabled",
        "disabled:hover:text-border-disabled"
      ]
    },
    size: {
      xs: "h-8 min-w-8 text-xs leading-[1.125rem] px-3 gap-2",
      sm: "h-9 min-w-9 text-sm leading-5 px-3.5 gap-2",
      md: "h-10 min-w-10 text-sm leading-5 px-4 gap-2",
      lg: "h-11 min-w-11 text-base leading-6 px-[1.125rem] gap-2",
      xl: "h-12 min-w-12 text-base leading-6 px-5 gap-2.5",
      "2xl": "h-16 min-w-16 text-lg leading-7 px-7 gap-3"
    },
    colorPalette: {
      gray: ""
    }
  },
  compoundVariants: [
    {
      variant: "solid",
      colorPalette: "gray",
      class: ["bg-gray-default text-gray-fg", "hover:bg-gray-emphasized", "focus-visible:outline-gray-default"]
    }
  ],
  defaultVariants: {
    variant: "solid",
    size: "md",
    colorPalette: "gray"
  }
});
const Button = forwardRef(
  ({ asChild, loading, disabled, loadingText, children, variant, size, colorPalette, className, ...rest }, ref) => {
    const trulyDisabled = loading || disabled;
    const Comp = asChild ? Slot : "button";
    const composed = buttonVariants({ variant, size, colorPalette, className });
    return /* @__PURE__ */ jsx(Comp, { ref, disabled: trulyDisabled, className: composed, ...rest, children: loading && !loadingText ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(ButtonSpinner, {}),
      /* @__PURE__ */ jsx("span", { className: "opacity-0", children })
    ] }) : loadingText ? loadingText : children });
  }
);
Button.displayName = "Button";
const ButtonSpinner = () => /* @__PURE__ */ jsx("div", { className: "inline-flex items-center justify-center absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx(Spinner, { colorPalette: "gray" }) });

export { Button };
